import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Autocomplete,
  FormControl,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import Swal from "sweetalert2";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import TravelStatusModal from "../../components/Common/TravelStatusModal";
import { baseurl, image, AdminBaseUrl } from "../../Basurl/Baseurl";
import {
  PATIENT_STATUS_OPTIONS,
  getPatientStatusLabel,
  isTravelStatus,
  normalizePatientStatus,
} from "../../utils/patientStatus";
import { toast } from "react-toastify";

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
});

/** Same airports API used by Travel create/edit (TravelStatusModal) */
const AIRPORTS_API = `${AdminBaseUrl}airports`;

const fetchAirportOptions = async () => {
  try {
    const res = await axios.get(AIRPORTS_API, { headers: authHeaders() });
    const body = res.data;
    const raw = body?.data ?? body?.airports ?? body ?? {};

    if (typeof raw === "object" && !Array.isArray(raw)) {
      return Object.entries(raw)
        .map(([code, name]) => `${code} - ${name}`)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));
    }

    if (Array.isArray(raw)) {
      return raw
        .map((a) => {
          const code = a.code ?? a.iata ?? "";
          const name = a.name ?? a.airportName ?? a.airport_name ?? "";
          return code && name ? `${code} - ${name}` : name || code;
        })
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));
    }

    return [];
  } catch {
    return [];
  }
};

const TRAVEL_TYPE_OPTIONS = [
  { value: "", label: "All Types" },
  { value: "Arriving", label: "Arriving" },
  { value: "Departing", label: "Departing" },
  { value: "Preponed", label: "Preponed" },
  { value: "Postponed", label: "Postponed" },
];

const EMPTY_TRAVEL_FILTERS = {
  travelDate: "",
  travelType: "",
  countryFrom: "",
  countryTo: "",
};

const TABS = [
  {
    key: "arriving",
    label: "Arriving Today",
    icon: "fa fa-plane",
    params: { p_status: "Arriving", today: "true" },
  },
  {
    key: "all",
    label: "All Travel Patients",
    icon: "fa fa-users",
    params: {},
  },
  {
    key: "departing",
    label: "Departing",
    icon: "fa fa-plane",
    params: { p_status: "Departing" },
  },
];

const buildTravelParams = (tabKey, filters = EMPTY_TRAVEL_FILTERS) => {
  const tab = TABS.find((t) => t.key === tabKey);
  const params = { ...(tab?.params || {}) };
  const travelDate = String(filters.travelDate || "").trim();
  const travelType = String(filters.travelType || "").trim();
  const countryFrom = String(filters.countryFrom || "").trim();
  const countryTo = String(filters.countryTo || "").trim();

  // Date filter overrides tab "today" shortcut
  if (travelDate) {
    params.travelDate = travelDate;
    delete params.today;
  }

  // Travel type / action dropdown overrides tab status when set
  if (travelType) {
    params.p_status = travelType;
    // If user picks Arrival/Departure with a date, don't force today
    if (travelDate) delete params.today;
  }

  // Airport values (same format as create: "DEL - Indira Gandhi…")
  if (countryFrom) {
    params.countryFrom = countryFrom;
    params.from = countryFrom;
  }
  if (countryTo) {
    params.countryTo = countryTo;
    params.to = countryTo;
  }

  return params;
};

const rowStatus = (row) =>
  normalizePatientStatus(row?.p_status || row?.status) || "";

const rowsForTab = (list, tabKey, filters = EMPTY_TRAVEL_FILTERS) => {
  const items = Array.isArray(list) ? list : [];
  // When API filters are active, trust server results (no client re-filter by tab)
  const hasApiFilters = Boolean(
    filters.travelDate ||
      filters.travelType ||
      filters.countryFrom ||
      filters.countryTo,
  );
  if (hasApiFilters) return items;
  if (tabKey === "departing") {
    return items.filter((row) => rowStatus(row) === "Departing");
  }
  if (tabKey === "arriving") {
    return items.filter((row) => rowStatus(row) === "Arriving");
  }
  return items;
};

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const filterFieldSx = {
  "& .MuiInputBase-root": { height: 38, background: "#fff" },
  "& .MuiInputBase-input": { padding: "6px 12px", fontSize: 13 },
};

export default function PatientTravel() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("arriving");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState({});
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(EMPTY_TRAVEL_FILTERS);
  const [draftFilters, setDraftFilters] = useState(EMPTY_TRAVEL_FILTERS);
  const [airportOptions, setAirportOptions] = useState([]);
  const [airportsLoading, setAirportsLoading] = useState(false);
  const [travelStatusModal, setTravelStatusModal] = useState({
    open: false,
    patientId: "",
    patientName: "",
    status: "",
  });
  const [travelStatusSubmitting, setTravelStatusSubmitting] = useState(false);

  const fetchTab = useCallback(async (tabKey, filterOverrides = null) => {
    const tab = TABS.find((t) => t.key === tabKey);
    if (!tab) return;
    const activeFilters = filterOverrides || filters;
    setLoading((prev) => ({ ...prev, [tabKey]: true }));
    try {
      const params = buildTravelParams(tabKey, activeFilters);
      const res = await axios.get(`${baseurl}get_travel_patients`, {
        headers: authHeaders(),
        params,
      });
      const body = res.data;
      const list = Array.isArray(body)
        ? body
        : Array.isArray(body?.data)
        ? body.data
        : body?.data
        ? [body.data]
        : [];
      setData((prev) => ({ ...prev, [tabKey]: list }));
    } catch (err) {
      toast.error(err?.response?.data?.message || `Failed to load ${tab.label}`);
      setData((prev) => ({ ...prev, [tabKey]: [] }));
    } finally {
      setLoading((prev) => ({ ...prev, [tabKey]: false }));
    }
  }, [filters]);

  const refreshAllTabs = useCallback(
    (filterOverrides = null) => {
      TABS.forEach((t) => fetchTab(t.key, filterOverrides));
    },
    [fetchTab],
  );

  useEffect(() => {
    refreshAllTabs();
    // Initial load only — later refreshes go through Apply / tab / refresh button
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    setAirportsLoading(true);
    fetchAirportOptions()
      .then((list) => {
        if (!cancelled) setAirportOptions(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setAirportOptions([]);
      })
      .finally(() => {
        if (!cancelled) setAirportsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const hasActiveFilters = useMemo(
    () =>
      Boolean(
        filters.travelDate ||
          filters.travelType ||
          filters.countryFrom ||
          filters.countryTo,
      ),
    [filters],
  );

  const handleApplyFilters = () => {
    setFilters(draftFilters);
    refreshAllTabs(draftFilters);
  };

  const handleClearFilters = () => {
    setDraftFilters(EMPTY_TRAVEL_FILTERS);
    setFilters(EMPTY_TRAVEL_FILTERS);
    refreshAllTabs(EMPTY_TRAVEL_FILTERS);
  };

  const updateDraftFilter = (field, value) => {
    setDraftFilters((prev) => ({ ...prev, [field]: value }));
  };

  const postPatientStatus = async (patientId, payload) => {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("Authorization token is missing");
    }

    const formData = new FormData();
    const statusVal = payload.p_status || payload.status;
    if (statusVal) {
      formData.append("p_status", statusVal);
    }
    if (payload.travelDetails) {
      const detailsStr = typeof payload.travelDetails === "object"
        ? JSON.stringify(payload.travelDetails)
        : payload.travelDetails;
      formData.append("travelDetails", detailsStr);
    }
    if (payload.travelDocuments) {
      const docs = Array.isArray(payload.travelDocuments)
        ? payload.travelDocuments
        : [payload.travelDocuments];
      docs.forEach((file) => {
        if (file) {
          formData.append("travelDocuments", file);
        }
      });
    }
    if (payload.travelDocumentLabels) {
      if (Array.isArray(payload.travelDocumentLabels)) {
        if (payload.travelDocumentLabels.length === 1) {
          formData.append("travelDocumentLabels", payload.travelDocumentLabels[0]);
        } else {
          formData.append("travelDocumentLabels", JSON.stringify(payload.travelDocumentLabels));
        }
      } else {
        formData.append("travelDocumentLabels", payload.travelDocumentLabels);
      }
    }

    const response = await axios.post(
      `${baseurl}changePatientStatus/${patientId}`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      },
    );
    if (response.status !== 200 && response.status !== 201) {
      throw new Error("Failed to update status. Please try again!");
    }
    return normalizePatientStatus(
      response.data?.data?.p_status ??
        response.data?.p_status ??
        response.data?.details?.p_status ??
        statusVal,
    );
  };

  const handleStatusChange = async (e, patientId, patientName = "") => {
    const newStatus = normalizePatientStatus(e.target.value);
    if (isTravelStatus(newStatus)) {
      setTravelStatusModal({
        open: true,
        patientId,
        patientName: patientName || "",
        status: newStatus,
      });
      return;
    }
    try {
      const savedStatus = await postPatientStatus(patientId, {
        p_status: newStatus,
      });
      // Update local state in all tabs
      setData((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((k) => {
          next[k] = (next[k] || []).map((row) =>
            row.patientId === patientId
              ? { ...row, p_status: savedStatus, status: savedStatus }
              : row,
          );
        });
        return next;
      });
      Swal.fire("Success!", "Patient status updated successfully!", "success");
      refreshAllTabs();
    } catch (err) {
      console.error("Status update error:", err);
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Something went wrong. Please try again!";
      toast.error(msg);
    }
  };

  const handleCloseTravelStatusModal = () => {
    if (travelStatusSubmitting) return;
    setTravelStatusModal({
      open: false,
      patientId: "",
      patientName: "",
      status: "",
    });
  };

  const handleSubmitTravelStatus = async (payload) => {
    const patientId = travelStatusModal.patientId;
    if (!patientId) return;
    setTravelStatusSubmitting(true);
    try {
      const savedStatus = await postPatientStatus(patientId, payload);
      setData((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((k) => {
          next[k] = (next[k] || []).map((row) =>
            row.patientId === patientId
              ? {
                  ...row,
                  p_status: savedStatus,
                  status: savedStatus,
                  travelDetails: payload.travelDetails || row.travelDetails,
                }
              : row,
          );
        });
        return next;
      });
      setTravelStatusModal({
        open: false,
        patientId: "",
        patientName: "",
        status: "",
      });
      Swal.fire("Success!", "Patient status updated successfully!", "success");
      refreshAllTabs();
    } catch (err) {
      console.error("Travel status error:", err);
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Failed to update travel status";
      toast.error(msg);
    } finally {
      setTravelStatusSubmitting(false);
    }
  };

  const currentTab = TABS.find((t) => t.key === activeTab);
  const rows = rowsForTab(data[activeTab], activeTab, filters);
  const isLoading = loading[activeTab];

  const filtered = rows.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      String(r.patient_name || r.patientName || r.name || "")
        .toLowerCase()
        .includes(q) ||
      String(r.patientId || "").toLowerCase().includes(q) ||
      String(r.p_status || r.status || "").toLowerCase().includes(q) ||
      String(r.countryFrom || r.country_from || r.fromCountry || "")
        .toLowerCase()
        .includes(q) ||
      String(r.countryTo || r.country_to || r.toCountry || "")
        .toLowerCase()
        .includes(q)
    );
  });

  return (
    <>
      <Navbar />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content" style={{ padding: "20px 24px" }}>
          {/* Page Header */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 16,
              marginBottom: 22,
            }}
          >
            <div>
              <h4
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: "#1a2233",
                  margin: "0 0 4px",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <i className="fa fa-plane" style={{ color: "#22c7b8" }} />
                Patient Travel
              </h4>
              <p style={{ fontSize: 13, color: "#7a8499", margin: 0 }}>
                Manage and track patient travel status
              </p>
            </div>
            {/* Summary pills */}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {TABS.map((t) => {
                const colors =
                  t.key === "arriving"
                    ? {
                        bg: "#ecfdf5",
                        border: "#a7f3d0",
                        num: "#059669",
                        lbl: "#6b7280",
                      }
                    : t.key === "all"
                    ? {
                        bg: "#eff6ff",
                        border: "#bfdbfe",
                        num: "#2563eb",
                        lbl: "#6b7280",
                      }
                    : {
                        bg: "#fffbeb",
                        border: "#fde68a",
                        num: "#d97706",
                        lbl: "#6b7280",
                      };
                return (
                  <div
                    key={t.key}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      padding: "8px 18px",
                      borderRadius: 10,
                      background: colors.bg,
                      border: `1px solid ${colors.border}`,
                      minWidth: 90,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 22,
                        fontWeight: 700,
                        lineHeight: 1,
                        color: colors.num,
                      }}
                    >
                      {loading[t.key] ? "…" : rowsForTab(data[t.key], t.key, filters).length}
                    </span>
                    <span
                      style={{
                        fontSize: 11,
                        color: colors.lbl,
                        marginTop: 2,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Tabs */}
          <div
            style={{
              display: "flex",
              gap: 4,
              borderBottom: "2px solid #eef0f5",
              marginBottom: 16,
              flexWrap: "wrap",
            }}
          >
            {TABS.map((t) => {
              const isActive = activeTab === t.key;
              const badgeBg = isActive
                ? t.key === "arriving"
                  ? "#d1fae5"
                  : t.key === "all"
                  ? "#dbeafe"
                  : "#fef3c7"
                : "#e5e7eb";
              const badgeColor = isActive
                ? t.key === "arriving"
                  ? "#059669"
                  : t.key === "all"
                  ? "#2563eb"
                  : "#d97706"
                : "#374151";
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setActiveTab(t.key)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    padding: "10px 18px",
                    border: "none",
                    cursor: "pointer",
                    fontSize: 14,
                    fontWeight: 500,
                    color: isActive ? "#22c7b8" : "#7a8499",
                    background: isActive ? "#f0fdf9" : "transparent",
                    borderBottom: isActive
                      ? "2px solid #22c7b8"
                      : "2px solid transparent",
                    marginBottom: -2,
                    borderRadius: "6px 6px 0 0",
                    transition: "all .15s",
                  }}
                >
                  <i className={t.icon} />
                  {t.label}
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: 22,
                      height: 20,
                      padding: "0 6px",
                      borderRadius: 20,
                      fontSize: 11,
                      fontWeight: 600,
                      background: badgeBg,
                      color: badgeColor,
                    }}
                  >
                    {loading[t.key] ? "…" : rowsForTab(data[t.key], t.key, filters).length}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Filter criteria — Date, Arrival/Departure, Country From/To */}
          <div
            style={{
              background: "#fff",
              border: "1px solid #eef0f5",
              borderRadius: 12,
              padding: "14px 16px",
              marginBottom: 14,
              boxShadow: "0 1px 4px rgba(0,0,0,.03)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                marginBottom: 12,
                flexWrap: "wrap",
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>
                <i className="fa fa-filter me-2" style={{ color: "#22c7b8" }} />
                Travel Filters
                {hasActiveFilters && (
                  <span
                    style={{
                      marginLeft: 8,
                      fontSize: 11,
                      fontWeight: 600,
                      color: "#0f766e",
                      background: "#ecfdf5",
                      border: "1px solid #a7f3d0",
                      borderRadius: 999,
                      padding: "2px 8px",
                    }}
                  >
                    Active
                  </span>
                )}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={handleClearFilters}
                  style={{
                    height: 34,
                    padding: "0 12px",
                    borderRadius: 8,
                    border: "1px solid #dce1e8",
                    background: "#fff",
                    color: "#64748b",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={handleApplyFilters}
                  style={{
                    height: 34,
                    padding: "0 14px",
                    borderRadius: 8,
                    border: "1px solid #0d9488",
                    background: "#0d9488",
                    color: "#fff",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  <i className="fa fa-search me-1" /> Apply Filters
                </button>
              </div>
            </div>

            <div className="row g-2">
              <div className="col-12 col-sm-6 col-lg-3">
                <label
                  style={{
                    display: "block",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#64748b",
                    marginBottom: 4,
                  }}
                >
                  Travel Date
                </label>
                <TextField
                  type="date"
                  size="small"
                  fullWidth
                  value={draftFilters.travelDate}
                  onChange={(e) => updateDraftFilter("travelDate", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={filterFieldSx}
                />
              </div>

              <div className="col-12 col-sm-6 col-lg-3">
                <label
                  style={{
                    display: "block",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#64748b",
                    marginBottom: 4,
                  }}
                >
                  Arrival / Departure
                </label>
                <FormControl fullWidth size="small">
                  <Select
                    value={draftFilters.travelType}
                    displayEmpty
                    onChange={(e) => updateDraftFilter("travelType", e.target.value)}
                    sx={{
                      height: 38,
                      background: "#fff",
                      fontSize: 13,
                    }}
                  >
                    {TRAVEL_TYPE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value || "all"} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              <div className="col-12 col-sm-6 col-lg-3">
                <label
                  style={{
                    display: "block",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#64748b",
                    marginBottom: 4,
                  }}
                >
                  Country From
                </label>
                <Autocomplete
                  options={airportOptions}
                  loading={airportsLoading}
                  value={draftFilters.countryFrom || null}
                  onChange={(_, newValue) =>
                    updateDraftFilter("countryFrom", newValue || "")
                  }
                  ListboxProps={{ style: { maxHeight: 250 } }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder={
                        airportsLoading
                          ? "Loading airports…"
                          : "e.g. MRU - Sir Seewoosagur…"
                      }
                      sx={filterFieldSx}
                    />
                  )}
                />
              </div>

              <div className="col-12 col-sm-6 col-lg-3">
                <label
                  style={{
                    display: "block",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#64748b",
                    marginBottom: 4,
                  }}
                >
                  Country To
                </label>
                <Autocomplete
                  options={airportOptions}
                  loading={airportsLoading}
                  value={draftFilters.countryTo || null}
                  onChange={(_, newValue) =>
                    updateDraftFilter("countryTo", newValue || "")
                  }
                  ListboxProps={{ style: { maxHeight: 250 } }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder={
                        airportsLoading
                          ? "Loading airports…"
                          : "e.g. DEL - Indira Gandhi…"
                      }
                      sx={filterFieldSx}
                    />
                  )}
                />
              </div>
            </div>
          </div>

          {/* Toolbar */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 14,
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                position: "relative",
                flex: 1,
                maxWidth: 380,
                minWidth: 200,
              }}
            >
              <i
                className="fa fa-search"
                style={{
                  position: "absolute",
                  left: 11,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#aab2c0",
                  fontSize: 13,
                }}
              />
              <input
                type="text"
                placeholder="Search by name, patient ID or status…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  width: "100%",
                  height: 38,
                  padding: "0 32px 0 34px",
                  border: "1px solid #dce1e8",
                  borderRadius: 8,
                  fontSize: 13,
                  background: "#fff",
                  outline: "none",
                }}
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    fontSize: 16,
                    color: "#aab2c0",
                    cursor: "pointer",
                  }}
                >
                  ×
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => refreshAllTabs()}
              title="Refresh"
              style={{
                height: 38,
                width: 38,
                border: "1px solid #dce1e8",
                borderRadius: 8,
                background: "#fff",
                cursor: "pointer",
                fontSize: 14,
                color: "#7a8499",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i className="fa fa-refresh" />
            </button>
          </div>

          {/* Table Card */}
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              border: "1px solid #eef0f5",
              boxShadow: "0 2px 8px rgba(0,0,0,.04)",
              overflow: "hidden",
            }}
          >
            {isLoading ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  padding: "60px 20px",
                  gap: 12,
                  color: "#aab2c0",
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    border: "3px solid #e0f7f5",
                    borderTopColor: "#22c7b8",
                    borderRadius: "50%",
                    animation: "pt-spin .7s linear infinite",
                  }}
                />
                <span>Loading {currentTab?.label}…</span>
              </div>
            ) : filtered.length === 0 ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  padding: "60px 20px",
                  gap: 10,
                  color: "#aab2c0",
                }}
              >
                <i
                  className="fa fa-plane"
                  style={{ fontSize: 40, color: "#d1d5db" }}
                />
                <p style={{ margin: 0, fontSize: 14 }}>No patients found</p>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table
                  key={activeTab}
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 13,
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        background: "#f8fafc",
                        borderBottom: "2px solid #eef0f5",
                      }}
                    >
                      {[
                        "#",
                        "Patient",
                        "Patient ID",
                        "Status",
                        "Travel Date",
                        "From",
                        "To",
                        "Arrival Time",
                        "Travellers",
                        "Services",
                        "Documents",
                        "Action",
                      ].map((h) => (
                        <th
                          key={h}
                          style={{
                            padding: "12px 14px",
                            textAlign: "left",
                            fontSize: 11,
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: ".5px",
                            color: "#7a8499",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((row, idx) => {
                      const td =
                        row.travelDetails &&
                        typeof row.travelDetails === "object"
                          ? row.travelDetails
                          : row;
                      const patientName =
                        row.patient_name || row.patientName || row.name || "—";
                      const currentStatus =
                        normalizePatientStatus(row.p_status || row.status) || "";
                      const rowKey = [
                        activeTab,
                        row._id || row.id || "",
                        row.patientId || "",
                        td?.travelDate || td?.travel_date || "",
                        idx,
                      ].join("-");

                      return (
                        <tr
                          key={rowKey}
                          style={{ borderBottom: "1px solid #f3f4f6" }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background = "#f8fafc")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background = "transparent")
                          }
                        >
                          <td style={{ padding: "11px 14px", color: "#aab2c0" }}>
                            {idx + 1}
                          </td>
                          <td
                            style={{
                              padding: "11px 14px",
                              fontWeight: 600,
                              color: "#1a2233",
                            }}
                          >
                            {patientName}
                          </td>
                          <td style={{ padding: "11px 14px" }}>
                            <span
                              style={{
                                display: "inline-block",
                                padding: "2px 8px",
                                background: "#f0f9ff",
                                color: "#0369a1",
                                borderRadius: 4,
                                fontSize: 12,
                                fontWeight: 600,
                                fontFamily: "monospace",
                              }}
                            >
                              {row.patientId || "—"}
                            </span>
                          </td>
                          {/* Status Select dropdown */}
                          <td style={{ padding: "6px 14px", minWidth: 150 }}>
                            <FormControl size="small" fullWidth>
                              <Select
                                value={currentStatus}
                                onChange={(e) =>
                                  handleStatusChange(
                                    e,
                                    row.patientId,
                                    patientName,
                                  )
                                }
                                displayEmpty
                                renderValue={(selected) =>
                                  getPatientStatusLabel(selected) ||
                                  selected ||
                                  "Select Status"
                                }
                                inputProps={{
                                  "aria-label": "Patient Status",
                                }}
                                style={{
                                  fontSize: 13,
                                  height: 34,
                                  background: "#fff",
                                  borderRadius: 6,
                                }}
                              >
                                {PATIENT_STATUS_OPTIONS.map((option) => (
                                  <MenuItem
                                    key={option.value}
                                    value={option.value}
                                    style={{ fontSize: 13 }}
                                  >
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </td>
                          <td style={{ padding: "11px 14px" }}>
                            {formatDate(td?.travelDate || td?.travel_date)}
                          </td>
                          <td
                            style={{
                              padding: "11px 14px",
                              maxWidth: 180,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            title={td?.from || ""}
                          >
                            {td?.from || "—"}
                          </td>
                          <td
                            style={{
                              padding: "11px 14px",
                              maxWidth: 180,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            title={td?.to || ""}
                          >
                            {td?.to || "—"}
                          </td>
                          <td style={{ padding: "11px 14px" }}>
                            {td?.arrivalTime || td?.arrival_time || "—"}
                          </td>
                          <td
                            style={{
                              padding: "11px 14px",
                              textAlign: "center",
                            }}
                          >
                            {td?.numberOfTravellers ||
                              td?.number_of_travellers ||
                              "—"}
                          </td>
                           <td style={{ padding: "11px 14px" }}>
                            {td?.services || td?.service || "—"}
                          </td>
                          <td style={{ padding: "11px 14px" }}>
                            {td?.documents && td.documents.length > 0 ? (
                              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                                {td.documents.map((doc, dIdx) => {
                                  const url = doc.url || doc.path || (doc.fileUrl ? `${image}uploads/${doc.fileUrl}` : doc.filePath ? `${image}${doc.filePath}` : "");
                                  const name = doc.label || doc.fileName || doc.name || `Doc ${dIdx + 1}`;
                                  return url ? (
                                    <a
                                      key={doc._id || dIdx}
                                      href={url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        background: "#f0faf9",
                                        border: "1px solid #c5f0eb",
                                        borderRadius: "4px",
                                        padding: "2px 6px",
                                        fontSize: "11px",
                                        color: "#1aa89b",
                                        textDecoration: "none",
                                        fontWeight: "500",
                                      }}
                                      title={name}
                                    >
                                      <i className="fa fa-file me-1" style={{ fontSize: "10px" }} />
                                      {name.length > 12 ? `${name.substring(0, 10)}...` : name}
                                    </a>
                                  ) : (
                                    <span
                                      key={doc._id || dIdx}
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        background: "#f3f4f6",
                                        border: "1px solid #e5e7eb",
                                        borderRadius: "4px",
                                        padding: "2px 6px",
                                        fontSize: "11px",
                                        color: "#4b5563",
                                      }}
                                    >
                                      {name}
                                    </span>
                                  );
                                })}
                              </div>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td style={{ padding: "11px 14px" }}>
                            <button
                              type="button"
                              onClick={() =>
                                navigate("/Admin/Patient-Detail", {
                                  state: {
                                    patientId: row.patientId,
                                    createdAt: row.createdAt || row.created_at,
                                  },
                                })
                              }
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 5,
                                background:
                                  "linear-gradient(135deg,#22c7b8 0%,#1aa89b 100%)",
                                color: "#fff",
                                border: "none",
                                borderRadius: 6,
                                padding: "6px 14px",
                                fontSize: 13,
                                fontWeight: 500,
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                              }}
                            >
                              <i className="fa fa-eye" /> View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <TravelStatusModal
        open={travelStatusModal.open}
        status={travelStatusModal.status}
        patientId={travelStatusModal.patientId}
        patientName={travelStatusModal.patientName}
        onClose={handleCloseTravelStatusModal}
        onSubmit={handleSubmitTravelStatus}
        submitting={travelStatusSubmitting}
      />

      <style>{`@keyframes pt-spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}
